import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { Loader2, Download, Receipt, CreditCard } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { downloadCsv } from "../lib/download";
import OrderDetailModal from "../components/OrderDetailModal";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

const CHANNEL_STYLE = {
  "Online": "bg-[#eefafa] text-[#1fb0b0]",
  "Box office": "bg-amber-100 text-amber-700",
  "Comp": "bg-gray-100 text-gray-500",
};

export default function AdminOrdersTab({ businesses }) {
  const [orders, setOrders] = useState(null);
  const [vendor, setVendor] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [payoutMonth, setPayoutMonth] = useState("");
  const [smsReport, setSmsReport] = useState(null);

  const load = () => {
    setOrders(null);
    const url = vendor === "all" ? "/admin/orders" : `/admin/orders?business_id=${vendor}`;
    api.get(url).then(({ data }) => setOrders(data.orders)).catch(() => setOrders([]));
  };
  useEffect(() => { load(); }, [vendor]);
  useEffect(() => {
    const q = payoutMonth ? `?month=${payoutMonth}` : "";
    api.get(`/admin/vendor-payouts${q}`).then(({ data }) => setPayouts(data.vendors)).catch(() => setPayouts([]));
  }, [payoutMonth]);
  useEffect(() => { api.get("/admin/sms-addon-report").then(({ data }) => setSmsReport(data)).catch(() => {}); }, []);

  const exportPayouts = async () => {
    try { await downloadCsv(`/admin/vendor-payouts.csv${payoutMonth ? `?month=${payoutMonth}` : ""}`, `vendor-payouts-${payoutMonth || "all-time"}.csv`); }
    catch { toast({ title: "Export failed", variant: "destructive" }); }
  };

  const rows = useMemo(() => {
    let r = orders || [];
    if (q.trim()) { const s = q.trim().toLowerCase(); r = r.filter((o) => [o.buyer_name, o.buyer_email, o.business_name, o.order_id, o.stripe_payment_intent].some((f) => (f || "").toLowerCase().includes(s))); }
    return r;
  }, [orders, q]);

  const totals = payouts.reduce((a, s) => ({ tickets: a.tickets + s.tickets, gross: a.gross + s.gross, fees: a.fees + s.fees, refunds: a.refunds + s.refunds, net: a.net + s.net }), { tickets: 0, gross: 0, fees: 0, refunds: 0, net: 0 });

  const exportCsv = async () => {
    try { await downloadCsv(vendor === "all" ? "/admin/orders.csv" : `/admin/orders.csv?business_id=${vendor}`, "all-orders.csv"); }
    catch { toast({ title: "Export failed", variant: "destructive" }); }
  };

  return (
    <div data-testid="admin-orders-tab">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <select value={vendor} onChange={(e) => setVendor(e.target.value)} data-testid="admin-order-vendor-filter"
          className="rounded-full px-4 py-2 text-sm border border-gray-200 bg-white text-[#16243b] outline-none focus:border-[#2ec9c9]">
          <option value="all">All vendors</option>
          {(businesses || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-order-search" placeholder="Search buyer, vendor or Stripe ref…"
          className="flex-1 min-w-[220px] rounded-full px-4 py-2 text-sm border border-gray-200 outline-none focus:border-[#2ec9c9]" />
        <button onClick={exportCsv} data-testid="admin-orders-export-csv" className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2"><Download size={15} /> Export CSV</button>
      </div>

      {!orders ? <Loader2 className="animate-spin text-[#2ec9c9]" size={30} /> : (
        <>
          {vendor === "all" && (payouts.length > 0 || payoutMonth) && (
            <div className="mb-5 bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto" data-testid="vendor-revenue-summary">
              <div className="px-5 pt-4 pb-2 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-semibold text-[#16243b]">Vendor payouts <span className="text-[#8a97a8] font-normal">— net = gross − fees − refunds</span></div>
                <div className="flex items-center gap-2">
                  <input type="month" value={payoutMonth} onChange={(e) => setPayoutMonth(e.target.value)} data-testid="payout-month"
                    className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-[#2ec9c9]" />
                  {payoutMonth && <button onClick={() => setPayoutMonth("")} className="text-xs text-[#8a97a8] hover:underline">All time</button>}
                  <button onClick={exportPayouts} data-testid="payout-export-csv" className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"><Download size={13} /> Export CSV</button>
                </div>
              </div>
              {smsReport && smsReport.count > 0 && (
                <p className="px-5 pb-1 text-xs text-[#1fb0b0]" data-testid="sms-addon-report">SMS add-on: {smsReport.count} vendor(s) enabled · +£{smsReport.monthly_revenue.toFixed(2)}/mo recurring</p>
              )}
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Vendor", "Tickets", "Gross sales", "Platform fees", "Refunds", "Net payable"].map((h) => <th key={h} className="text-left px-5 py-2.5 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {payouts.map((s) => (
                    <tr key={s.business_id} data-testid={`vendor-summary-${s.vendor}`} className="border-t border-gray-50">
                      <td className="px-5 py-2.5 font-medium text-[#16243b]">{s.vendor}</td>
                      <td className="px-5 py-2.5 text-[#5a6b80]">{s.tickets}{s.refunded_tickets ? <span className="text-red-400 text-xs"> (−{s.refunded_tickets})</span> : ""}</td>
                      <td className="px-5 py-2.5 text-[#5a6b80]">£{s.gross.toFixed(2)}</td>
                      <td className="px-5 py-2.5 text-[#5a6b80]">£{s.fees.toFixed(2)}</td>
                      <td className="px-5 py-2.5 text-red-500">£{s.refunds.toFixed(2)}</td>
                      <td className="px-5 py-2.5 text-[#16243b] font-semibold" data-testid={`vendor-net-${s.vendor}`}>£{s.net.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-100 bg-[#f9fbfd] font-semibold">
                    <td className="px-5 py-2.5 text-[#16243b]">All vendors</td>
                    <td className="px-5 py-2.5 text-[#16243b]">{totals.tickets}</td>
                    <td className="px-5 py-2.5 text-[#16243b]">£{totals.gross.toFixed(2)}</td>
                    <td className="px-5 py-2.5 text-[#16243b]">£{totals.fees.toFixed(2)}</td>
                    <td className="px-5 py-2.5 text-[#16243b]">£{totals.refunds.toFixed(2)}</td>
                    <td className="px-5 py-2.5 text-[#16243b]">£{totals.net.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Vendor", "Buyer", "Event", "Purchased", "Tickets", "Channel", "Total", "Stripe", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.order_id} data-testid={`admin-order-row-${o.order_id}`} className="border-t border-gray-50 hover:bg-[#f9fbfd] cursor-pointer" onClick={() => setSelected(o)}>
                  <td className="px-5 py-3 text-[#16243b] font-medium">{o.business_name}</td>
                  <td className="px-5 py-3"><div className="text-[#16243b]">{o.buyer_name || "—"}</div><div className="text-xs text-[#8a97a8]">{o.buyer_email}</div></td>
                  <td className="px-5 py-3 text-[#5a6b80]">{o.product_title}</td>
                  <td className="px-5 py-3 text-[#8a97a8] whitespace-nowrap">{fmtDate(o.purchased_at)}</td>
                  <td className="px-5 py-3 text-[#16243b]">{o.ticket_count}</td>
                  <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${CHANNEL_STYLE[o.channel] || "bg-gray-100 text-gray-500"}`}>{o.channel}</span></td>
                  <td className="px-5 py-3 text-[#16243b] font-semibold">£{o.total.toFixed(2)}</td>
                  <td className="px-5 py-3 text-[#8a97a8]">{o.stripe_payment_intent ? <span className="inline-flex items-center gap-1 font-mono text-xs" title={o.stripe_payment_intent}><CreditCard size={12} className="text-[#635bff]" />{o.stripe_payment_intent.slice(0, 12)}…</span> : "—"}</td>
                  <td className="px-5 py-3 text-right"><Receipt size={16} className="text-[#2ec9c9] inline" /></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-[#8a97a8]">No orders found.</td></tr>}
            </tbody>
          </table>
          </div>
        </>
      )}

      {selected && <OrderDetailModal order={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
